import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ButtonPrimary from '@thrivetrm/ui/components/ButtonPrimary';
import Form from '@thrivetrm/ui/components/Form';
import FormDropzone from 'core/components/Form.Dropzone';
import LoadingContainer from '@thrivetrm/ui/components/LoadingContainer';
import { isValidEmailAddress } from '../forms/validators/emailAddress';
import { useApiGet } from '@thrivetrm/ui/hooks/useApi';
import formDataPropType from '../../propTypes/formDataPropType';
import { formatPrivacyPolicyUrl } from '../../util';

const MAX_RESUME_FILE_SIZE_MB = 7.5;
const KB = 1024;
const BYTES_IN_MB = KB * KB;

const ALLOWED_EXTENSIONS = [
  'doc',
  'docx',
  'pdf',
  'csv',
  'xlsx',
  'ppt',
  'pptx',
  'xls',
  'xlsm',
  'jpg',
  'jpeg',
  'png',
  'mp3',
  'mp4',
  'rtf',
  'txt',
  'TXT',
];

const ApplyProfessionalInfoForm = ({
  caslCompanyName,
  caslPrivacyPolicyUrl,
  formData,
  hasVoluntaryIdentificationStatusEnabled,
  isApplicationLoading,
  onClose,
  onSubmit,
}) => {
  const [
    loadContactSources,
    isLoadingContactSources,
    _contactSourcesLoadError,
    contactSources,
  ] = useApiGet('/api/v1/contact_sources');

  useEffect(() => {
    loadContactSources();
  }, [loadContactSources]);

  const applyConsentText = useSelector((state) =>
    state.customer.get('applyConsentText'),
  );

  const isCaslActive = useSelector((state) =>
    state.customer.getIn(['flags', 'casl']),
  );

  const customerName = useSelector((state) => state.customer.get('name'));

  const contactSourceOptions = contactSources?.contact_sources
    .map((contactSource) => ({
      label: contactSource.name,
      value: contactSource.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const contactSourcesWithDetailsRequired =
    contactSources?.contact_sources.filter(
      (contactSource) => contactSource.details === true,
    );

  const privacyUrl = formatPrivacyPolicyUrl(caslPrivacyPolicyUrl);

  const validateEmailAddress = (emailAddress) =>
    isValidEmailAddress(emailAddress)
      ? null
      : 'Please enter a valid email address';

  const validateConsent = (consent) =>
    isCaslActive && !consent ? 'Your consent is required' : null;

  const [resumeError, setResumeError] = useState(null);

  const [coverLetterError, setCoverLetterError] = useState(null);

  const [resumeFile, setResumeFile] = useState(formData.resume || null);

  const [coverLetterFile, setCoverLetterFile] = useState(
    formData.coverLetter || null,
  );

  const formRef = useRef(null);

  const validateFile = (file, isResume = true) => {
    if (!file) {
      return null;
    }

    const extension = file.name?.split('.').pop()?.toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return `You are not allowed to upload “.${extension}” files. Allowed types: ${ALLOWED_EXTENSIONS.join(
        ', ',
      )}`;
    }

    if (file.size > MAX_RESUME_FILE_SIZE_MB * BYTES_IN_MB) {
      return isResume
        ? `Resume file size must be less than ${MAX_RESUME_FILE_SIZE_MB} MB`
        : `Cover Letter file size must be less than ${MAX_RESUME_FILE_SIZE_MB} MB`;
    }

    return null;
  };

  const handleResumeChange = (event) => {
    const file = event.target?.files?.[0];

    if (!file) {
      setResumeFile(null);
      setResumeError(null);
      return;
    }

    const error = validateFile(file, true);
    if (error) {
      setResumeFile(null);
      setResumeError(error);
    } else {
      setResumeFile(file);
      setResumeError(null);
    }
  };

  const handleCoverLetterChange = (event) => {
    const file = event.target?.files?.[0];

    if (!file) {
      setCoverLetterFile(null);
      setCoverLetterError(null);
      return;
    }

    const error = validateFile(file, false);
    if (error) {
      setCoverLetterFile(null);
      setCoverLetterError(error);
    } else {
      setCoverLetterFile(file);
      setCoverLetterError(null);
    }
  };

  useEffect(() => {
    const node = formRef.current;
    if (!node) {
      return () => {};
    }

    const handleClick = (e) => {
      const deleteBtn = e.target.closest('[data-testid="delete button"]');
      if (!deleteBtn) {
        return;
      }

      const field = deleteBtn.closest('[data-field]');
      if (!field) {
        return;
      }

      const which = field.getAttribute('data-field');
      if (which === 'resume') {
        setResumeError(null);
      }
      if (which === 'coverLetter') {
        setCoverLetterError(null);
      }
    };

    node.addEventListener('click', handleClick);
    return () => node.removeEventListener('click', handleClick);
  }, []);

  return (
    <Form onSubmit={onSubmit}>
      <div ref={formRef}>
        <LoadingContainer isLoading={isLoadingContactSources} minHeight='100%'>
          <Form.TextInput
            className='u-marginBottom-12'
            initialValue={formData.firstName}
            inputWidth='full'
            label='First Name'
            name='firstName'
            rules={{
              required: {
                value: true,
                message: 'First Name is required',
              },
            }}
          />

          <Form.TextInput
            className='u-marginBottom-12'
            initialValue={formData.lastName}
            inputWidth='full'
            label='Last Name'
            name='lastName'
            rules={{
              required: {
                value: true,
                message: 'Last Name is required',
              },
            }}
          />

          <Form.TextInput
            className='u-marginBottom-12'
            initialValue={formData.email}
            inputWidth='full'
            label='Email Address'
            name='email'
            rules={{
              validate: validateEmailAddress,
              required: {
                value: true,
                message: 'Email is required',
              },
            }}
          />

          <Form.TextInput
            className='u-marginBottom-12'
            initialValue={formData.phone}
            inputWidth='full'
            label='Mobile Phone'
            name='phone'
          />

          {contactSourceOptions?.length > 0 && (
            <Form.Select
              className='u-marginBottom-12'
              data-testid='contact-source-input'
              initialValue={formData.contactSourceId}
              inputWidth='full'
              label='How did you hear about this job?'
              name='contactSourceId'
              options={contactSourceOptions}
              rules={{
                required: { value: true, message: 'Referral is required' },
              }}
              size='medium'
            />
          )}

          <Form.FieldDependentContent
            shouldRender={(fields) =>
              contactSourcesWithDetailsRequired
                ?.map((source) => source.id)
                .includes(fields.contactSourceId)
            }
          >
            <Form.TextInput
              className='u-marginBottom-12'
              initialValue={formData.sourceDetails}
              inputWidth='full'
              label='Who referred you?'
              name='sourceDetails'
              rules={{
                required: {
                  value: true,
                  message: 'Referred by is required',
                },
              }}
            />
          </Form.FieldDependentContent>

          <Form.TextInput
            className='u-marginBottom-12'
            initialValue={formData.linkedInUrl}
            inputWidth='full'
            label='LinkedIn Public Profile URL'
            name='linkedInUrl'
          />

          <div className='u-marginTop-16'>
            <div className='row'>
              <div className='col-sm-6' data-field='resume'>
                <FormDropzone
                  initialValue={resumeFile}
                  label='Resume/CV'
                  name='resume'
                  onChange={handleResumeChange}
                  rules={{
                    required: {
                      value: true,
                      message: 'Resume/CV is required',
                    },
                  }}
                />
                {resumeError && (
                  <div className='u-textColor-red u-fontSize-small'>
                    {resumeError}
                  </div>
                )}
              </div>

              <div className='col-sm-6' data-field='coverLetter'>
                <FormDropzone
                  initialValue={coverLetterFile}
                  label='Cover Letter'
                  name='coverLetter'
                  onChange={handleCoverLetterChange}
                />
                {coverLetterError && (
                  <div className='u-textColor-red u-fontSize-small'>
                    {coverLetterError}
                  </div>
                )}
              </div>
            </div>
          </div>

          {applyConsentText && (
            <>
              <Form.Checkbox
                className='u-marginVertical-16 u-marginLeft-n'
                initialValue={formData.consentProvided}
                label={applyConsentText}
                name='consentProvided'
                rules={{
                  validate: validateConsent,
                  required: {
                    value: true,
                  },
                }}
              />
              <Form.Checkbox
                className='u-marginVertical-16 u-marginLeft-n'
                initialValue={formData.smsOptIn}
                label={`I consent to receive SMS messages from ${customerName} regarding this position. I understand that I may opt-out of SMS messaging at any time.`}
                name='smsOptIn'
              />
              {caslCompanyName && privacyUrl && (
                <div className='u-marginTop-16'>
                  <a
                    href={privacyUrl}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    View Privacy Policy for {caslCompanyName}
                  </a>
                </div>
              )}
            </>
          )}

          <div className='u-flex u-flexJustify-r'>
            <ButtonPrimary isOutline={true} label='Cancel' onClick={onClose} />
            <Form.SubmitButton
              isDisabled={
                isApplicationLoading ||
                Boolean(resumeError) ||
                Boolean(coverLetterError)
              }
              isLoading={isApplicationLoading}
              label={
                hasVoluntaryIdentificationStatusEnabled ? 'Next' : 'Submit'
              }
            />
          </div>
        </LoadingContainer>
      </div>
    </Form>
  );
};

ApplyProfessionalInfoForm.defaultProps = {
  caslCompanyName: null,
  caslPrivacyPolicyUrl: null,
  formData: null,
  hasVoluntaryIdentificationStatusEnabled: false,
  onClose: null,
};

ApplyProfessionalInfoForm.propTypes = {
  caslCompanyName: PropTypes.string,
  caslPrivacyPolicyUrl: PropTypes.string,
  formData: formDataPropType,
  hasVoluntaryIdentificationStatusEnabled: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
};

export default ApplyProfessionalInfoForm;
